import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Clock } from '../components/SharedComponents';
import { getDailyUnits, getVader } from '../qlik';

// ── JOURNALEN ─────────────────────────────────────────────────────
// Magnus privata dagbok — hårdlåst till hans e-post i firestore.rules
// (inte ens andra admins). En notis per dag, fritext. Notiserna blir
// AI-underlag: budgetassistenten får samma period ifjol, veckobrevet
// får veckans + fjolårets notiser. Skriv som till dig själv — AI:n
// instrueras att inte återge internt känsligt i personaltexter.

const ACCENT = '#4A3B2A'; // läderbrun — dagbokskänsla
const idagISO = () => new Date().toISOString().slice(0, 10);
const tkr = (v) => `${Math.round(v / 1000).toLocaleString('sv-SE')} tkr`;

// Kompakt vädertext för en dag ur cachens byDay: "−3…+2°C · 4,2 mm · byar 14 m/s"
function vaderTextAv(dag) {
  if (!dag) return '';
  const delar = [];
  const fmt = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(Math.round(v * 10) / 10).toLocaleString('sv-SE')}`;
  if (typeof dag.min === 'number' && typeof dag.max === 'number') delar.push(`${fmt(dag.min)}…${fmt(dag.max)}°C`);
  if (dag.regn > 0) delar.push(`${dag.regn.toLocaleString('sv-SE')} mm`);
  if (dag.byarMax > 12) delar.push(`byar ${Math.round(dag.byarMax)} m/s`);
  return delar.join(' · ');
}

const VECKODAG = ['sön', 'mån', 'tis', 'ons', 'tor', 'fre', 'lör'];
function datumLabel(iso) {
  const d = new Date(iso + 'T12:00:00');
  return `${VECKODAG[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1} ${d.getFullYear()}`;
}

export default function JournalBlock() {
  const [datum, setDatum] = useState(idagISO());
  const [text, setText] = useState('');
  const [sparadText, setSparadText] = useState(''); // senast sparade (för dirty-koll)
  const [notiser, setNotiser] = useState(null);     // [{datum, text}] senaste, fallande
  const [sparar, setSparar] = useState(false);
  const [msg, setMsg] = useState('');
  const [fel, setFel] = useState('');
  const [dagsSiffror, setDagsSiffror] = useState(null); // minnesstöd: intäkt per enhet
  const [vader, setVader] = useState(null);             // cachens vader-doc (nu + byDay)
  // Referensdokument (kalendarium, paket, kampanjer …)
  const [dok, setDok] = useState(null);         // [{id, namn, text, skapadMs}]
  const [dokNamn, setDokNamn] = useState('');
  const [dokText, setDokText] = useState('');
  const [dokBirger, setDokBirger] = useState(false); // dela som Birger-kunskap i alla samtal
  const [dokMsg, setDokMsg] = useState('');
  const [dokOpen, setDokOpen] = useState(false);     // dokumentkortet hopfällt som default
  const filRef = useRef(null);

  const dirty = text !== sparadText;

  const laddaNotiser = async () => {
    const snap = await getDocs(query(collection(db, 'intel_journal'), orderBy('datum', 'desc'), limit(60)));
    const ut = [];
    snap.forEach((d) => { const v = d.data(); if (v.text) ut.push({ datum: v.datum, text: v.text, vaderText: v.vaderText || '' }); });
    setNotiser(ut);
  };
  const laddaDok = async () => {
    const snap = await getDocs(query(collection(db, 'intel_journal'), where('typ', '==', 'dokument')));
    const ut = [];
    snap.forEach((d) => { const v = d.data(); if (v.text) ut.push({ id: d.id, namn: v.namn || 'namnlöst', text: v.text, skapadMs: v.skapadMs || 0, birger: !!v.birger }); });
    ut.sort((a, b) => b.skapadMs - a.skapadMs);
    setDok(ut);
  };
  useEffect(() => {
    laddaNotiser().catch((e) => { console.error(e); setFel('Kunde inte hämta notiserna'); });
    laddaDok().catch(console.error);
    getVader().then(setVader).catch(() => {});
  }, []);

  // Vädertext för vald dag (byDay byggs framåt av prefetchen)
  const dagensVader = useMemo(() => vaderTextAv(vader?.byDay?.[datum]), [vader, datum]);

  // Exportera hela journalen som markdown-fil (notiser + dokumentlista)
  const exportera = async () => {
    const snap = await getDocs(collection(db, 'intel_journal'));
    const notiserAlla = [], dokAlla = [];
    snap.forEach((d) => {
      const v = d.data();
      if (v.typ === 'dokument') { if (v.text) dokAlla.push(v); }
      else if (v.datum && v.text) notiserAlla.push(v);
    });
    notiserAlla.sort((a, b) => a.datum.localeCompare(b.datum));
    dokAlla.sort((a, b) => (a.skapadMs || 0) - (b.skapadMs || 0));
    let md = `# Journalen — Bruksvallsliden\n\nExporterad ${idagISO()} · ${notiserAlla.length} notiser · ${dokAlla.length} dokument\n\n## Notiser\n\n`;
    for (const n of notiserAlla) md += `### ${n.datum} (${datumLabel(n.datum)})\n\n${n.text}\n\n`;
    if (dokAlla.length) {
      md += `\n## Referensdokument\n\n`;
      for (const d of dokAlla) md += `### ${d.namn || 'namnlöst'}\n\n${d.text}\n\n`;
    }
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `journalen-bruksvallsliden-${idagISO()}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Dokument: läs textfil till formuläret / spara / ta bort
  const lasDokFil = async (file) => {
    setDokMsg('');
    if (file.size > 400000) { setDokMsg('Filen är för stor (max ~400 kB text). PDF/Word/bilder stöds inte än — klistra in texten i stället.'); return; }
    try {
      const t = await file.text();
      if (!t.trim() || t.includes('\u0000')) { setDokMsg('Kunde inte läsa filen som text. PDF/Word/bilder stöds inte än — klistra in innehållet i textrutan i stället.'); return; }
      setDokNamn(file.name.replace(/\.(txt|md|csv)$/i, ''));
      setDokText(t.slice(0, 300000));
    } catch { setDokMsg('Kunde inte läsa filen'); }
  };
  const sparaDok = async () => {
    const namn = dokNamn.trim() || 'namnlöst';
    const t = dokText.trim();
    if (!t) return;
    await setDoc(doc(db, 'intel_journal', `dok_${Date.now()}`), {
      typ: 'dokument', namn, text: t.slice(0, 300000),
      birger: dokBirger,
      skapad: serverTimestamp(), skapadMs: Date.now(),
    });
    setDokNamn(''); setDokText(''); setDokBirger(false);
    setDokMsg(dokBirger ? '✓ Sparat — Birger minns det nu i alla samtal' : '✓ Sparat — AI:n har det nu som referens');
    if (filRef.current) filRef.current.value = '';
    setTimeout(() => setDokMsg(''), 3000);
    laddaDok().catch(console.error);
  };
  const taBortDok = async (id) => {
    await deleteDoc(doc(db, 'intel_journal', id));
    laddaDok().catch(console.error);
  };

  // Ladda vald dags notis
  useEffect(() => {
    let live = true;
    (async () => {
      const s = await getDoc(doc(db, 'intel_journal', datum));
      if (!live) return;
      const t = s.exists() ? (s.data().text || '') : '';
      setText(t); setSparadText(t); setMsg(''); setFel('');
    })().catch(console.error);
    return () => { live = false; };
  }, [datum]);

  // Minnesstöd: dagens intäkter per enhet (ur cachen)
  useEffect(() => {
    let live = true;
    (async () => {
      const units = await getDailyUnits();
      if (!live) return;
      const per = {};
      for (const u of units) {
        if (u.datum !== datum) continue;
        per[u.enhet] = (per[u.enhet] || 0) + (u.intakt || 0);
      }
      const rader = Object.entries(per).filter(([, v]) => v > 500).sort((a, b) => b[1] - a[1]).slice(0, 6);
      setDagsSiffror(rader.length ? rader : null);
    })().catch(() => setDagsSiffror(null));
    return () => { live = false; };
  }, [datum]);

  const spara = async () => {
    if (sparar) return;
    setSparar(true); setFel(''); setMsg('');
    try {
      const t = text.trim();
      if (t) {
        // Vädret bakas in i notisen — historiken äger sitt väder för alltid
        await setDoc(doc(db, 'intel_journal', datum), {
          datum, text: t, updatedAt: serverTimestamp(),
          ...(dagensVader ? { vaderText: dagensVader } : {}),
        });
        setSparadText(text); setMsg('✓ Sparad');
      } else {
        await deleteDoc(doc(db, 'intel_journal', datum));
        setSparadText(''); setMsg('Notisen borttagen');
      }
      laddaNotiser().catch(console.error);
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setFel(`Kunde inte spara: ${e.message}`);
    } finally { setSparar(false); }
  };

  // Samma dag ifjol (minus ett år) — kul och nyttig återblick
  const ifjolNotis = useMemo(() => {
    if (!notiser) return null;
    const d = new Date(datum + 'T12:00:00');
    d.setFullYear(d.getFullYear() - 1);
    const mal = d.toISOString().slice(0, 10);
    return notiser.find((n) => n.datum === mal) || null;
  }, [notiser, datum]);

  return (
    <div className="screen">
      <div className="header" style={{ background: ACCENT }}>
        <div>
          <div className="header-title">Bara för dina ögon — och portalens AI</div>
          <div className="header-name">Journalen</div>
        </div>
        <Clock />
      </div>

      {/* Skriv dagens notis */}
      <div className="card" data-noexport="1">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>📖 {datumLabel(datum)}</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="date" value={datum} max={idagISO()} onChange={(e) => e.target.value && setDatum(e.target.value)}
              style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '5px 8px', fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)' }} />
            {datum !== idagISO() && (
              <button onClick={() => setDatum(idagISO())}
                style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)' }}>
                Idag
              </button>
            )}
          </div>
        </div>

        {(dagsSiffror || dagensVader) && (
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 8, lineHeight: 1.6 }}>
            {dagensVader && <>Väder: {dagensVader}{dagsSiffror ? <br /> : null}</>}
            {dagsSiffror && <>Intäkter: {dagsSiffror.map(([e, v]) => `${e} ${tkr(v)}`).join(' · ')}</>}
          </div>
        )}

        <textarea value={text} onChange={(e) => setText(e.target.value)}
          placeholder={'Hur var dagen? Skriv som till dig själv — tre meningar räcker.\nT.ex. "Fullt hus, norska bussgruppen kom. Brasserien slet med bemanningen på kvällen. Regn imorgon — förväntar lugnare fjälltur-dag."'}
          style={{ width: '100%', minHeight: 110, border: '1px solid var(--border)', borderRadius: 8, padding: 10, fontSize: 13.5, lineHeight: 1.55, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', resize: 'vertical' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: fel ? '#A32D2D' : '#2E7D32', fontFamily: 'system-ui,sans-serif' }}>{fel || msg}</span>
          <button onClick={spara} disabled={!dirty || sparar}
            style={{ background: dirty ? ACCENT : 'transparent', color: dirty ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 18px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
            {sparar ? 'Sparar…' : '💾 Spara'}
          </button>
        </div>

        {ifjolNotis && (
          <div style={{ marginTop: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 8, fontFamily: 'system-ui,sans-serif' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 3 }}>
              För ett år sedan — {datumLabel(ifjolNotis.datum)}{ifjolNotis.vaderText ? ` · ${ifjolNotis.vaderText}` : ''}
            </div>
            <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{ifjolNotis.text}</div>
          </div>
        )}

        <div className="note">
          Journalen är hårdlåst till din inloggning — inte ens andra administratörer kan läsa den.
          Notiserna blir underlag för AI:n i dina egna verktyg: budgetassistenten får samma period ifjol, veckobrevet får veckans och fjolårets notiser.
        </div>
      </div>

      {/* Referensdokument — kalendarium, paket, kampanjer m.m. (dragspel) */}
      <div className="card" data-noexport="1">
        <div onClick={() => setDokOpen((o) => !o)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: dokOpen ? 'rotate(90deg)' : 'none', marginRight: 6 }}>▸</span>
            📎 Referensdokument — kalendarium, paket, kampanjer …
          </div>
          {!dokOpen && (
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
              {dok === null ? '…' : `${dok.length} dokument${dok.some((x) => x.birger) ? ` · ${dok.filter((x) => x.birger).length} delas med Birger` : ''}`}
            </span>
          )}
        </div>
        {dokOpen && (<div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 8, lineHeight: 1.5 }}>
          Dokument som AI:n alltid har som referens — t.ex. årskalendariet, paketutbudet eller kampanjplanen.
          Ladda upp en textfil (.txt/.md/.csv) eller klistra in innehållet. PDF/Word/bilder stöds inte än — klistra in texten så länge.
        </div>
        <input value={dokNamn} onChange={(e) => setDokNamn(e.target.value)} placeholder='Namn, t.ex. "Kalendarium 26/27" eller "Paket & kampanjer vintern"'
          style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 12.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', marginBottom: 6 }} />
        <textarea value={dokText} onChange={(e) => setDokText(e.target.value)}
          placeholder={'Innehållet — t.ex.\nv.44 Höstlovsveckan, familjepaket\nv.51–1 Jul & nyår, julbord fre–sön\nv.7–12 Sportlov, temakvällar Brasserie ons…'}
          style={{ width: '100%', minHeight: 80, border: '1px solid var(--border)', borderRadius: 8, padding: 8, fontSize: 12.5, lineHeight: 1.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', resize: 'vertical' }} />
        <label style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginTop: 8, fontSize: 12, fontFamily: 'system-ui,sans-serif', cursor: 'pointer', color: 'var(--text)' }}>
          <input type="checkbox" checked={dokBirger} onChange={(e) => setDokBirger(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            <strong>Dela med Birger</strong> — blir företagskunskap i <em>alla</em> hans samtal (historia, fakta, arbetssätt).
            <span style={{ color: 'var(--text-secondary)' }}> Utan bock: bara referens i dina egna AI-verktyg.</span>
          </span>
        </label>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              …eller läs in från fil (.txt/.md/.csv):
              <input type="file" ref={filRef} accept=".txt,.md,.csv" onChange={(e) => e.target.files[0] && lasDokFil(e.target.files[0])}
                style={{ fontSize: 11, fontFamily: 'system-ui,sans-serif', maxWidth: 210 }} />
            </label>
            <span style={{ fontSize: 12, color: dokMsg.startsWith('✓') ? '#2E7D32' : '#A36B00', fontFamily: 'system-ui,sans-serif' }}>{dokMsg}</span>
          </div>
          <button onClick={sparaDok} disabled={!dokText.trim()}
            style={{ background: dokText.trim() ? ACCENT : 'transparent', color: dokText.trim() ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
            📎 Spara dokument
          </button>
        </div>
        {dok !== null && dok.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {dok.map((d) => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid var(--border)', fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{d.namn}{d.birger ? <span title="Delas med Birger i alla samtal" style={{ marginLeft: 6, fontStyle: 'italic', fontFamily: 'Georgia,serif' }}>· B</span> : null}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 520 }}>
                    {Math.round(d.text.length / 1000) || '<1'} k tecken · {d.text.slice(0, 90).replace(/\s+/g, ' ')}…
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => { setDokNamn(d.namn); setDokText(d.text); setDokBirger(d.birger); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    title="Öppna i redigeringsrutan (spara skapar en ny version — ta bort den gamla)"
                    style={{ border: '1px solid var(--border)', borderRadius: 7, padding: '3px 9px', fontSize: 11.5, cursor: 'pointer', background: 'transparent', color: 'var(--text)', fontFamily: 'system-ui,sans-serif' }}>
                    Öppna
                  </button>
                  <button onClick={() => taBortDok(d.id)} title="Ta bort dokumentet"
                    style={{ border: '1px solid var(--border)', borderRadius: 7, padding: '3px 9px', fontSize: 11.5, cursor: 'pointer', background: 'transparent', color: '#A32D2D', fontFamily: 'system-ui,sans-serif' }}>
                    Ta bort
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="note">
          Dokumenten följer med som referens i budgetassistenten och styrelseutkastet (senaste 12, upp till ~4 000 tecken totalt — håll dem korta och aktuella).
        </div>
        </div>)}
      </div>

      {/* Tidigare notiser */}
      <div className="card" data-noexport="1">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Tidigare notiser</div>
          <button onClick={() => exportera().catch((e) => setFel('Export misslyckades: ' + e.message))}
            title="Ladda ner hela journalen (alla notiser + dokument) som markdown-fil"
            style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', background: 'transparent', color: 'var(--text)', fontFamily: 'system-ui,sans-serif' }}>
            ⤓ Exportera allt
          </button>
        </div>
        {notiser === null && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Hämtar…</div>}
        {notiser !== null && !notiser.length && (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
            Tomt än så länge — dagens notis blir din första. Om ett år tackar du dig själv.
          </div>
        )}
        {(notiser || []).map((n) => (
          <div key={n.datum} onClick={() => setDatum(n.datum)}
            style={{ padding: '8px 0', borderTop: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}
            title="Klicka för att läsa/redigera">
            <div style={{ fontSize: 11, fontWeight: 700, color: n.datum === datum ? ACCENT : 'var(--text-secondary)', marginBottom: 2 }}>
              {datumLabel(n.datum)}{n.vaderText ? <span style={{ fontWeight: 400 }}> · {n.vaderText}</span> : null}
            </div>
            <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {n.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
